// PRPathways.js - Portugal
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, MapPin, Users, ChevronDown, ChevronUp } from 'lucide-react';

const PRPathways = () => {
  const [expandedPathway, setExpandedPathway] = useState(null);

  const pathways = [
    {
      icon: Zap,
      title: 'Golden Visa (Investment)',
      subtitle: 'Qualify through job creation, real estate or capital investment',
      description: 'The route most investors take. You make a qualifying investment, and in exchange the stay requirement stays light at 7 days a year.',
      details: {
        overview: 'Non-EU nationals who put qualifying money into Portugal are granted a residency permit under this programme, and that permit in time leads on to permanent residency and citizenship.',
        programs: [
          {
            name: 'Capital Transfer (€500,000)',
            requirements: 'Move at least €500,000 into a Portuguese bank account, or invest that sum in Portuguese companies. It has to stay in place for 5 years. Of all the options, this one leaves you the widest choice over where the money sits.'
          },
          {
            name: 'Investment Funds (€500,000)',
            requirements: 'Put €500,000 into qualifying funds that back Portuguese companies. The fund itself has to be approved by the Portuguese authorities, and you hold the position for a minimum of 5 years.'
          },
          {
            name: 'Job Creation (10 jobs)',
            requirements: 'Create 10 full-time jobs in Portugal, filled by Portuguese nationals or legal residents, and keep them running for 5 years. This one tends to suit entrepreneurs and business owners.'
          },
          {
            name: 'Cultural Heritage Donation (€250,000)',
            requirements: 'Give €250,000 or more towards the arts, Portuguese cultural heritage or national heritage preservation. The contribution cannot be refunded.'
          },
          {
            name: 'Scientific Research (€500,000)',
            requirements: 'Commit €500,000 to research carried out by Portuguese scientific institutions, public or private, and keep it there for 5 years.'
          }
        ],
        keyPoints: [
          'Processing time: 6-12 months for the initial application',
          'Stay requirement of 7 days a year, averaged across the 5 years',
          'Free movement through the Schengen Area',
          'A spouse and dependent children can be added by family reunification',
          'Permanent residency opens up after 5 years',
          'Citizenship after 5 years, provided you have basic Portuguese'
        ]
      }
    },
    {
      icon: MapPin,
      title: 'D7 Visa (Passive Income)',
      subtitle: 'Aimed at retirees, remote workers and anyone living on passive income',
      description: 'A residency visa for people whose income arrives without local work: pensions, rent, investment returns or a remote job.',
      details: {
        overview: 'The D7 was written for non-EU nationals who can keep themselves in Portugal on income they already have, rather than on a Portuguese salary.',
        programs: [
          {
            name: 'Retirement/Pension Income',
            requirements: 'Show a steady pension or retirement income at or above the Portuguese minimum wage, which means €820 a month for the main applicant, 50% of that for a spouse and 30% for each child. Twelve months of history is needed behind it, along with evidence that it will continue.'
          },
          {
            name: 'Rental Income',
            requirements: 'Recurring rent from property you own. Deeds and tenancy agreements go in with the application, together with proof that the income is stable and clears the threshold.'
          },
          {
            name: 'Investment Income',
            requirements: 'Dividends, interest or returns from other holdings. Bank statements and portfolio documentation have to show the stream is reliable rather than one-off.'
          },
          {
            name: 'Remote Work',
            requirements: 'A contract with an employer outside Portugal that permits remote working and pays at least the minimum income threshold, plus a letter from that employer confirming the arrangement.'
          }
        ],
        keyPoints: [
          'Processing time: 3-6 months',
          'Financially far less demanding than the Golden Visa',
          'You need to be in Portugal for at least 16 months of the first 2-year period',
          'Renewed every 2 years, with permanent residency at the 5-year mark',
          'Portuguese healthcare and education become available to you',
          'NHR tax treatment can apply, running for 10 years'
        ]
      }
    },
    {
      icon: Users,
      title: 'Family Reunification & Other Visas',
      subtitle: 'Family reunification, D2 entrepreneur, D3 highly qualified',
      description: 'Further routes covering entrepreneurs, highly skilled employees and the relatives of people already resident in Portugal.',
      details: {
        overview: 'Beyond the Golden Visa and the D7, Portugal runs a set of other residency categories, each with conditions and advantages of its own.',
        programs: [
          {
            name: 'D2 Entrepreneur/Business Visa',
            requirements: 'You bring a business plan, either for a Portuguese company or for self-employment, backed by capital that usually falls between €5,000 and €10,000. The case turns on showing the venture is viable, that it benefits Portugal economically, and that it will create jobs or deliver services.'
          },
          {
            name: 'D3 Highly Qualified Activity',
            requirements: 'A Portuguese employer offers you a highly skilled role paying above the national average, generally €1,500 a month or more. You supply the qualifications and the experience; the employer explains why the role needs a foreign hire.'
          },
          {
            name: 'Family Reunification',
            requirements: 'Open to the spouse, children or parents of a Portuguese citizen or legal resident. You evidence the relationship and the family tie, while the sponsor shows stable income and somewhere to live. No investment comes into it.'
          }
        ],
        keyPoints: [
          'Processing times run 2-6 months, depending on which visa you use',
          'Both the D2 and the D3 require you to be physically present in Portugal',
          'Family reunification sits alongside every one of these visa types',
          'Each of these routes reaches permanent residency at 5 years',
          'Citizenship calls for Portuguese at A2 level',
          'Public services and travel within the EU are included'
        ]
      }
    }
  ];

  const togglePathway = (index) => {
    setExpandedPathway(expandedPathway === index ? null : index);
  };

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            Ways Into Portuguese Residency
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Which route makes sense comes down to where your income comes from and how much time you plan to spend in the country.
          </p>
        </motion.div>

        <div className="space-y-6">
          {pathways.map((pathway, index) => {
            const Icon = pathway.icon;
            const isExpanded = expandedPathway === index;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => togglePathway(index)}
                  className="w-full p-6 flex items-start gap-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon size={24} className="text-white" />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-primary mb-2">
                      {pathway.title}
                    </h3>
                    <p className="text-sm text-gray-500 font-light mb-2">
                      {pathway.subtitle}
                    </p>
                    <p className="text-gray-600 text-sm font-light">
                      {pathway.description}
                    </p>
                  </div>

                  <div className="flex-shrink-0">
                    {isExpanded ? (
                      <ChevronUp size={24} className="text-secondary" />
                    ) : (
                      <ChevronDown size={24} className="text-secondary" />
                    )}
                  </div>
                </button>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 border-t border-gray-200">
                        <div className="pt-6 space-y-6">
                          <div>
                            <h4 className="text-base font-semibold text-primary mb-2">
                              Overview
                            </h4>
                            <p className="text-gray-600 text-sm font-light leading-relaxed">
                              {pathway.details.overview}
                            </p>
                          </div>

                          <div>
                            <h4 className="text-base font-semibold text-primary mb-3">
                              Routes and What They Require
                            </h4>
                            <div className="space-y-4">
                              {pathway.details.programs.map((program, pIndex) => (
                                <div key={pIndex} className="bg-surface p-4 rounded-lg">
                                  <h5 className="text-sm font-semibold text-primary mb-2">
                                    {program.name}
                                  </h5>
                                  <p className="text-gray-600 text-sm font-light leading-relaxed">
                                    {program.requirements}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div>
                            <h4 className="text-base font-semibold text-primary mb-3">
                              The Essentials
                            </h4>
                            <ul className="space-y-2">
                              {pathway.details.keyPoints.map((point, kIndex) => (
                                <li key={kIndex} className="flex items-start gap-2">
                                  <span className="w-1.5 h-1.5 bg-secondary rounded-full mt-2 flex-shrink-0"></span>
                                  <span className="text-gray-600 text-sm font-light">
                                    {point}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PRPathways;
